import React, { useState } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";

const loanLabelMap = {
  PL: "Personal Loan",
  BL: "Business Loan",
  AL: "Car Loan",
  HL: "Home Loan",
  LAP: "Loan Against Property",
};

export default function CallOutcomeForm({ onSubmit, submitting }) {
  const [callStatus, setCallStatus] = useState("");
  const [disposition, setDisposition] = useState("");
  const [callbackTime, setCallbackTime] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loanType, setLoanType] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [employmentType, setEmploymentType] = useState("");

  const showDisposition = callStatus === "Connected";
  const showCallback = showDisposition && disposition === "Call Back";
  const showLoanDetails = showDisposition && disposition === "Interested";

  const handleSubmit = () => {
    onSubmit?.({
      callStatus,
      disposition,
      callbackTime,
      remarks,
      loanType,
      loanAmount,
      monthlyIncome,
      employmentType,
    });
  };

  return (
    <Box sx={{ mx: "auto", display: "grid", gap: 2 }}>
      {/* Call Status */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography sx={{ minWidth: 140 }}>Call Status:</Typography>
        <RadioGroup
          row
          value={callStatus}
          onChange={(e) => {
            setCallStatus(e.target.value);
            setDisposition("");
            setCallbackTime("");
          }}
        >
          {["Connected", "No Answer", "Switched Off", "Not Working"].map((s) => (
            <FormControlLabel key={s} value={s} control={<Radio />} label={s} />
          ))}
        </RadioGroup>
      </Box>

      {/* Disposition */}
      {showDisposition && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography sx={{ minWidth: 140 }}>Disposition:</Typography>
          <RadioGroup
            row
            value={disposition}
            onChange={(e) => setDisposition(e.target.value)}
          >
            {["Interested", "Not Interested", "Call Back"].map((opt) => (
              <FormControlLabel
                key={opt}
                value={opt}
                control={<Radio />}
                label={opt}
              />
            ))}
          </RadioGroup>
        </Box>
      )}

      {/* Callback Time */}
      {showCallback && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography sx={{ minWidth: 140 }}>Callback Time:</Typography>
          <TextField
            type="datetime-local"
            value={callbackTime}
            onChange={(e) => setCallbackTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ flex: 1 }}
          />
        </Box>
      )}

      {/* Loan Details */}
      {showLoanDetails && (
        <>
          {/* Loan Type */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ minWidth: 140 }}>Loan Type:</Typography>
            <TextField
              select
              size="small"
              value={loanType}
              onChange={(e) => setLoanType(e.target.value)}
              displayEmpty
              sx={{ flex: 1 }}
              SelectProps={{
                renderValue: (selected) =>
                  selected && loanLabelMap[selected]
                    ? loanLabelMap[selected]
                    : "Select loan type",
              }}
            >
              {Object.entries(loanLabelMap).map(([code, label]) => (
                <MenuItem key={code} value={code}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Loan Amount */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ minWidth: 140 }}>Req Loan Amt:</Typography>
            <TextField
              type="number"
              size="small"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Monthly Income */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ minWidth: 140 }}>Monthly Income:</Typography>
            <TextField
              type="number"
              size="small"
              value={monthlyIncome}
              onChange={(e) => setMonthlyIncome(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Employment Type */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ minWidth: 140 }}>Employment:</Typography>
            <TextField
              select
              size="small"
              value={employmentType}
              onChange={(e) => setEmploymentType(e.target.value)}
              displayEmpty
              sx={{ flex: 1 }}
              SelectProps={{
                renderValue: (selected) =>
                  selected ? selected : "Select Employment",
              }}
            >
              {["Salaried", "Self-Employed", "Business Owner"].map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </>
      )}

      {/* Remarks */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography sx={{ minWidth: 140 }}>Remarks:</Typography>
        <TextField
          multiline
          rows={2}
          size="small"
          sx={{ flex: 1 }}
          placeholder="Add remarks here..."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      </Box>

      {/* Submit Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={<CheckCircle />}
          sx={{ px: 3, py: 1.2, borderRadius: 3, textTransform: "none" }}
        >
          {submitting ? "Saving..." : "Save Outcome"}
        </Button>
      </Box>
    </Box>
  );
}